import { FC, useState, useCallback } from 'react';
import intl from 'react-intl-universal';
import { Table, tailwindOverride, Typography } from '@getsynapse/design-system';
import { LDUser } from 'utils/customTypes';
import MembersTableHeader from './MembersTableHeader';
import MembersTableBody from './MembersTableBody';
import RemoveMemberConfirmationModal from '../RemoveMemberConfirmationModal/RemoveMemberConfirmationModal';

interface MembersTableListProps {
  learningTeamMembers: LDUser[];
  removeTeamMember: (userId: string) => void;
}

const MembersTableList: FC<MembersTableListProps> = ({
  learningTeamMembers,
  removeTeamMember,
}) => {
  const [selectedMemberIdToRemove, setSelectedMemberIdToRemove] = useState<
    string | null
  >(null);

  const toggleRemoveMemberConfirmationModal = useCallback(
    (memberId: string | null) => {
      setSelectedMemberIdToRemove(memberId);
    },
    []
  );

  const confirmRemoveMember = useCallback(async () => {
    await removeTeamMember(selectedMemberIdToRemove!);
    toggleRemoveMemberConfirmationModal(null);
  }, [
    removeTeamMember,
    selectedMemberIdToRemove,
    toggleRemoveMemberConfirmationModal,
  ]);

  return (
    <>
      {selectedMemberIdToRemove && (
        <RemoveMemberConfirmationModal
          onClose={() => toggleRemoveMemberConfirmationModal(null)}
          onConfirm={confirmRemoveMember}
        />
      )}
      <Table
        aria-label={intl.get(
          'ORG_SETTINGS_PAGE.LEARNING_TEAM_DETAILS_PAGE.MEMBERS_TABLE.ARIA_LABEL'
        )}
        className={tailwindOverride('relative table-fixed w-full', {
          'max-h-96 overflow-y-auto': learningTeamMembers.length,
        })}
        data-cy='learning-team-members_table'
        canSelectRows={false}
        emptyComponent={
          <Typography className='h-12 flex justify-center items-center text-neutral'>
            {intl.get('EDIT_TEAM.NO_TEAM_MEMBERS')}
          </Typography>
        }
        data={{
          headData: {
            headCells: [],
          },
          rows: [],
          total: learningTeamMembers.length,
        }}
      >
        <MembersTableHeader />
        <MembersTableBody
          learningTeamMembers={learningTeamMembers}
          onRemoveMember={toggleRemoveMemberConfirmationModal}
        />
      </Table>
    </>
  );
};

export default MembersTableList;
